import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import type { ServiceProps, ServiceRef } from './Service.data';
import styles from './Service.module.scss';
import {
  setupTransitionInTimelineText,
  setupTransitionOutTimelineText,
} from './Service.transitions';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import PrimaryButton from '../../../atoms/primary-button/PrimaryButton';
import { useGlobalStore } from '../../../../stores/global';
import { isTouch } from '../../../../hooks/useIsDesktopDevice';
import { getLink } from '../../../../util/url.utils';
import { useRouter } from 'next/router';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import type { TextRef } from '../../../atoms/text/Text.data';
import type { PrimaryButtonRef } from '../../../atoms/primary-button/PrimaryButton.data';

export default function Service({
  isVisible,
  className,
  blok,
  index,
  ref,
}: ServiceProps & { ref?: Ref<ServiceRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const indexRef = useRef<HeadingRef>(null);
  const titleRef = useRef<HeadingRef>(null);
  const subtitleRef = useRef<HeadingRef>(null);
  const descriptionRef = useRef<TextRef>(null);
  const linkRef = useRef<PrimaryButtonRef>(null);

  const globalStore = useGlobalStore();
  const router = useRouter();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        indexRef,
        titleRef,
        subtitleRef,
        descriptionRef,
        linkRef,
      },
      setupTransitionInTimeline: setupTransitionInTimelineText,
      setupTransitionOutTimeline: setupTransitionOutTimelineText,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  const link = getLink(blok?.link, router.locale);

  return (
    <div ref={elementRef} className={classNames(styles.service, className)} {...sbEditable(blok)}>
      <Heading
        as="h5"
        size="heading-05"
        className={classNames(styles.index)}
        disableTriggerInOnScroll
        transitionOutType={isTouch ? 'splitWordsSlideOutUp' : 'splitCharsSlideOutUp'}
        transitionInType={isTouch ? 'splitWordsSlideInUp' : 'splitCharsSlideInUp'}
        ref={indexRef}
      >
        {`${index}`.padStart(2, '0')}
      </Heading>
      <Heading
        as="h2"
        size="heading-02" // the word 'transformation' in heading-02 is too big for mobile screens
        className={classNames(styles.title)}
        disableTriggerInOnScroll
        transitionOutType={isTouch ? 'splitWordsSlideOutUp' : 'splitCharsSlideOutUp'}
        transitionInType={isTouch ? 'splitWordsSlideInUp' : 'splitCharsSlideInUp'}
        ref={titleRef}
      >
        {blok.title}
      </Heading>
      <Heading
        as="h3"
        size="label-small"
        className={classNames(styles.subtitle)}
        disableTriggerInOnScroll
        transitionOutType={isTouch ? 'splitWordsSlideOutUp' : 'splitCharsSlideOutUp'}
        transitionInType={isTouch ? 'splitWordsSlideInUp' : 'splitCharsSlideInUp'}
        ref={subtitleRef}
      >
        {blok.subtitle}
      </Heading>
      <Text
        as="p"
        size="body-standard"
        className={classNames(styles.description)}
        disableTriggerInOnScroll
        transitionInType="splitWordsSlideInUp"
        transitionOutType="splitWordsSlideOutUp"
        ref={descriptionRef}
      >
        {blok.description}
      </Text>

      {link && (
        <div>
          <PrimaryButton
            ref={linkRef}
            className={classNames(styles.button)}
            link={{ href: link }}
            label={globalStore.globalSettings.view_service}
            disableTriggerInOnScroll
          />
        </div>
      )}
    </div>
  );
}
